// Parent Q&A Management Component for Admin Dashboard
import React, { useState, useEffect } from 'react';
import './qamanagement.css'; // Reuse same CSS
import { API_BASE } from '../services/api.js';

const ParentQAManagement = ({ onBack }) => {
  // State for parent questions and form
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingParent, setEditingParent] = useState(null);

  // Hierarchy data
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [chunks, setChunks] = useState([]);
  const [microChunks, setMicroChunks] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    question_stem: '',
    hindi_question_stem: '',
    order_index: 0,
    
    // For hierarchy selection
    selected_subject_id: '',
    selected_chapter_id: '',
    selected_topic_id: '',
    selected_subtopic_id: '',
    selected_chunk_id: '',
    selected_micro_chunk_id: ''
  });

  // Load initial data
  useEffect(() => {
    loadParents();
    loadSubjects();
  }, []);

  const loadSubjects = async () => {
    try {
      const response = await fetch(`${API_BASE}/subjects/`);
      const data = await response.json();
      setSubjects(data);
    } catch (err) {
      console.error('Error loading subjects:', err);
    }
  };

  const loadChapters = async (subjectId) => {
    try {
      const response = await fetch(`${API_BASE}/chapters/?subject_id=${subjectId}`);
      const data = await response.json();
      setChapters(data);
    } catch (err) {
      console.error('Error loading chapters:', err);
    }
  };

  const loadTopics = async (chapterId) => {
    try {
      const response = await fetch(`${API_BASE}/topics/?chapter_id=${chapterId}`);
      const data = await response.json();
      setTopics(data);
    } catch (err) {
      console.error('Error loading topics:', err);
    }
  };

  const loadSubtopics = async (topicId) => {
    try {
      const response = await fetch(`${API_BASE}/subtopics/?topic_id=${topicId}`);
      const data = await response.json();
      setSubtopics(data);
    } catch (err) {
      console.error('Error loading subtopics:', err);
    }
  };

  const loadChunks = async (topicId) => {
    try {
      const response = await fetch(`${API_BASE}/chunks/?topic_id=${topicId}`);
      const data = await response.json();
      setChunks(data);
    } catch (err) {
      console.error('Error loading chunks:', err);
    }
  };

  const loadMicroChunks = async (chunkId) => {
    try {
      const response = await fetch(`${API_BASE}/micro-chunks/?chunk_id=${chunkId}`);
      const data = await response.json();
      setMicroChunks(data);
    } catch (err) {
      console.error('Error loading micro chunks:', err);
    }
  };

  const loadParents = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/predefined-qa-parent/`);
      if (!response.ok) throw new Error('Failed to load parent questions');
      const data = await response.json();
      setParents(data);
    } catch (err) {
      setError(`Error loading parent questions: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubjectChange = async (subjectId) => {
    setFormData(prev => ({
      ...prev,
      selected_subject_id: subjectId,
      selected_chapter_id: '',
      selected_topic_id: '',
      selected_subtopic_id: '',
      selected_chunk_id: '',
      selected_micro_chunk_id: ''
    }));
    
    // Reset dependent dropdowns first
    setChapters([]);
    setTopics([]);
    setSubtopics([]);
    setChunks([]);
    setMicroChunks([]);
    
    // Then load new data
    if (subjectId) {
      await loadChapters(subjectId);
    }
  };

  const handleChapterChange = async (chapterId) => {
    setFormData(prev => ({
      ...prev,
      selected_chapter_id: chapterId,
      selected_topic_id: '',
      selected_subtopic_id: '',
      selected_chunk_id: '',
      selected_micro_chunk_id: ''
    }));
    
    // Reset dependent dropdowns first
    setTopics([]);
    setSubtopics([]);
    setChunks([]);
    setMicroChunks([]);
    
    // Then load new data
    if (chapterId) {
      await loadTopics(chapterId);
    }
  };

  const handleTopicChange = async (topicId) => {
    setFormData(prev => ({
      ...prev,
      selected_topic_id: topicId,
      selected_subtopic_id: '',
      selected_chunk_id: '',
      selected_micro_chunk_id: ''
    }));
    
    // Reset dependent dropdowns first
    setSubtopics([]);
    setMicroChunks([]);
    
    // Then load new data
    if (topicId) {
      await loadSubtopics(topicId);
      await loadChunks(topicId);
    }
  };

  const handleSubtopicChange = async (subtopicId) => {
    setFormData(prev => ({
      ...prev,
      selected_subtopic_id: subtopicId,
      selected_chunk_id: '',
      selected_micro_chunk_id: ''
    }));
    setMicroChunks([]);
  };

  const handleChunkChange = async (chunkId) => {
    setFormData(prev => ({
      ...prev,
      selected_chunk_id: chunkId,
      selected_micro_chunk_id: ''
    }));
    
    setMicroChunks([]);
    
    if (chunkId) {
      await loadMicroChunks(chunkId);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.question_stem.trim()) {
      setError('Question stem is required');
      return;
    }

    if (!formData.selected_subject_id || !formData.selected_chapter_id || !formData.selected_topic_id) {
      setError('Subject, Chapter, and Topic are required');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        question_stem: formData.question_stem.trim(),
        hindi_question_stem: formData.hindi_question_stem ? formData.hindi_question_stem.trim() : null,
        subject_id: parseInt(formData.selected_subject_id),
        chapter_id: parseInt(formData.selected_chapter_id),
        topic_id: parseInt(formData.selected_topic_id),
        subtopic_id: formData.selected_subtopic_id ? parseInt(formData.selected_subtopic_id) : null,
        chunk_id: formData.selected_chunk_id ? parseInt(formData.selected_chunk_id) : null,
        micro_chunk_id: formData.selected_micro_chunk_id ? parseInt(formData.selected_micro_chunk_id) : null,
        order_index: parseInt(formData.order_index) || 0
      };

      const url = editingParent 
        ? `${API_BASE}/predefined-qa-parent/${editingParent.parent_id}`
        : `${API_BASE}/predefined-qa-parent/`;
      
      const method = editingParent ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      setSuccess(editingParent 
        ? 'Parent question updated successfully!'
        : 'Parent question created successfully!'
      );
      
      // Reset form
      resetForm();
      
      // Reload parents
      loadParents();
      
    } catch (err) {
      setError(`Error ${editingParent ? 'updating' : 'creating'} parent question: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (parent) => {
    setEditingParent(parent);
    
    setFormData({
      question_stem: parent.question_stem,
      hindi_question_stem: parent.hindi_question_stem || '',
      order_index: parent.order_index || 0,
      selected_subject_id: parent.subject_id ? parent.subject_id.toString() : '',
      selected_chapter_id: parent.chapter_id ? parent.chapter_id.toString() : '',
      selected_topic_id: parent.topic_id ? parent.topic_id.toString() : '',
      selected_subtopic_id: parent.subtopic_id ? parent.subtopic_id.toString() : '',
      selected_chunk_id: parent.chunk_id ? parent.chunk_id.toString() : '',
      selected_micro_chunk_id: parent.micro_chunk_id ? parent.micro_chunk_id.toString() : ''
    });
    
    setShowCreateForm(true);
    setError(null);
    setSuccess(null);
  };

  const handleDelete = async (parent) => {
    if (!window.confirm(`Are you sure you want to delete this parent question?\n\n"${parent.question_stem}"\n\nThis action cannot be undone!`)) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/predefined-qa-parent/${parent.parent_id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      setSuccess('Parent question deleted successfully!');
      loadParents();
      
    } catch (err) {
      setError(`Error deleting parent question: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      question_stem: '',
      hindi_question_stem: '',
      order_index: 0,
      selected_subject_id: '',
      selected_chapter_id: '',
      selected_topic_id: '',
      selected_subtopic_id: '',
      selected_chunk_id: '',
      selected_micro_chunk_id: ''
    });
    setEditingParent(null);
    setShowCreateForm(false);
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="qa-management">
      {/* Header */}
      <div className="management-header">
        <div className="header-left">
          <button className="back-btn" onClick={onBack}>
            ← Back to Q&A Management
          </button>
          <h2>Parent Question Management</h2>
        </div>
        <button 
          className="add-btn"
          onClick={() => setShowCreateForm(!showCreateForm)}
          disabled={loading}
        >
          {showCreateForm ? 'Cancel' : '+ Add Parent Question'}
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          {success}
          <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}
      
      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">❌</span>
          {error}
          <button className="alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="qa-form-container">
          <div className="form-card">
            <h3>{editingParent ? 'Edit Parent Question' : 'Create New Parent Question'}</h3>
            <p className="upload-description">
              Parent questions are the common stems for multi-part questions. Child questions reference the parent_id in Excel uploads.
            </p>
            
            <form onSubmit={handleSubmit} className="qa-form">
              {/* Question Stems */}
              <div className="form-section">
                <h4>Question Stem (Common Part)</h4>
                
                <div className="form-group">
                  <label>English Question Stem *</label>
                  <textarea
                    name="question_stem"
                    value={formData.question_stem}
                    onChange={handleInputChange}
                    placeholder="e.g., Which of the following are intermediate goods or final goods?"
                    rows="3"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Hindi Question Stem (Optional)</label>
                  <textarea
                    name="hindi_question_stem"
                    value={formData.hindi_question_stem}
                    onChange={handleInputChange}
                    placeholder="हिंदी में प्रश्न स्टेम..."
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Order Index</label>
                  <input
                    type="number"
                    name="order_index"
                    value={formData.order_index}
                    onChange={handleInputChange}
                    placeholder="Display order (0, 1, 2...)"
                    min="0"
                  />
                  <p className="help-text">Controls the display order of parent questions</p>
                </div>
              </div>

              {/* Link to Hierarchy */}
              {!editingParent && (  // ADD THIS LINE - Only show when creating, not editing
              <div className="form-section">
                <h4>Link to Hierarchy *</h4>
                
                <div className="form-group">
                  <label>Select Subject *</label>
                  <select
                    name="selected_subject_id"
                    value={formData.selected_subject_id}
                    onChange={(e) => handleSubjectChange(e.target.value)}
                    required
                  >
                    <option value="">Choose Subject...</option>
                    {subjects.map(subject => (
                      <option key={subject.subject_id} value={subject.subject_id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.selected_subject_id && (
                  <div className="form-group">
                    <label>Select Chapter *</label>
                    <select
                      name="selected_chapter_id"
                      value={formData.selected_chapter_id}
                      onChange={(e) => handleChapterChange(e.target.value)}
                      required
                    >
                      <option value="">Choose Chapter...</option>
                      {chapters.map(chapter => (
                        <option key={chapter.chapter_id} value={chapter.chapter_id}>
                          {chapter.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.selected_chapter_id && (
                  <div className="form-group">
                    <label>Select Topic *</label>
                    <select
                      name="selected_topic_id"
                      value={formData.selected_topic_id}
                      onChange={(e) => handleTopicChange(e.target.value)}
                      required
                    >
                      <option value="">Choose Topic...</option>
                      {topics.map(topic => (
                        <option key={topic.topic_id} value={topic.topic_id}>
                          {topic.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.selected_topic_id && (
                  <div className="form-group">
                    <label>Select Subtopic (Optional)</label>
                    <select
                      name="selected_subtopic_id"
                      value={formData.selected_subtopic_id}
                      onChange={(e) => handleSubtopicChange(e.target.value)}
                    >
                      <option value="">Choose Subtopic (Optional)...</option>
                      {subtopics.map(subtopic => (
                        <option key={subtopic.subtopic_id} value={subtopic.subtopic_id}>
                          {subtopic.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.selected_topic_id && (
                  <div className="form-group">
                    <label>Link to Chunk (Optional)</label>
                    <select
                      name="selected_chunk_id"
                      value={formData.selected_chunk_id}
                      onChange={(e) => handleChunkChange(e.target.value)}
                    >
                      <option value="">Choose Chunk (Optional)...</option>
                      {chunks.map(chunk => (
                        <option key={chunk.chunk_id} value={chunk.chunk_id}>
                          Chunk {chunk.chunk_id} - {chunk.chunk_text?.substring(0, 50)}...
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.selected_chunk_id && (
                  <div className="form-group">
                    <label>Link to Micro Chunk (Optional)</label>
                    <select
                      name="selected_micro_chunk_id"
                      value={formData.selected_micro_chunk_id}
                      onChange={handleInputChange}
                    >
                      <option value="">Choose Micro Chunk (Optional)...</option>
                      {microChunks.map(microChunk => (
                        <option key={microChunk.micro_chunk_id} value={microChunk.micro_chunk_id}>
                          Micro Chunk {microChunk.micro_chunk_id} - {microChunk.content?.substring(0, 50)}...
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}  {/* ADD THIS LINE - Close the conditional */} 

            
              {/* Show hierarchy info in edit mode (read-only) */}
              {editingParent && (
                <div className="form-section">
                  <h4>Linked Hierarchy (Cannot be edited)</h4>
                  <div className="qa-link-info" style={{ padding: '15px', background: '#f8f9fa' }}>
                    <strong>Linked to:</strong> Subject {editingParent.subject_id} → Chapter {editingParent.chapter_id} → Topic {editingParent.topic_id}
                    {editingParent.subtopic_id && ` → Subtopic ${editingParent.subtopic_id}`}
                    {editingParent.chunk_id && ` → Chunk ${editingParent.chunk_id}`}
                    {editingParent.micro_chunk_id && ` → Micro Chunk ${editingParent.micro_chunk_id}`}
                  </div>
                  <p className="help-text" style={{ color: '#856404', background: '#fff3cd', padding: '10px', borderRadius: '4px', marginTop: '10px' }}>
                    ⚠️ Hierarchy cannot be edited to maintain consistency with linked child questions.
                  </p>
                </div>
              )} 
            
              {/* Form Actions */}
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? 'Processing...' : editingParent ? 'Update Parent Question' : 'Create Parent Question'}
                </button>
                <button type="button" className="cancel-btn" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Parent Questions List */}
      <div className="qa-list-container">
        <h3>Existing Parent Questions ({parents.length})</h3>
        <p className="upload-description">
          Use the Parent ID when uploading child questions via Excel. Copy the ID from the list below.
        </p>
        
        {loading && !showCreateForm && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading parent questions...</p>
          </div>
        )}

        {!loading && parents.length === 0 && (
          <div className="empty-state">
            <p>No parent questions found. Create your first parent question!</p>
          </div>
        )}

        {!loading && parents.length > 0 && (
          <div className="qa-grid">
            {parents.map(parent => (
              <div key={parent.parent_id} className="qa-card">
                <div className="qa-card-header">
                  <div className="qa-meta">
                    <span className="qa-id">Parent ID: {parent.parent_id}</span>
                    <span className="question-type-badge">Order: {parent.order_index}</span>
                  </div>
                  <div className="qa-actions">
                    <button 
                      className="action-btn edit"
                      onClick={() => handleEdit(parent)}
                      title="Edit Parent Question"
                    >
                      ✏️
                    </button>
                    <button 
                      className="action-btn delete"
                      onClick={() => handleDelete(parent)}
                      title="Delete Parent Question"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                <div className="qa-content">
                  <div className="qa-question">
                    <strong>Question Stem (EN):</strong> {parent.question_stem}
                  </div>
                  
                  {parent.hindi_question_stem && (
                    <div className="qa-hindi-section">
                      <div className="qa-question">
                        <strong>Question Stem (HI):</strong> {parent.hindi_question_stem}
                      </div>
                    </div>
                  )}
                  
                  {/* Hierarchy Info */}
                  <div className="qa-link-info">
                    <strong>Linked to:</strong> Subject {parent.subject_id} → Chapter {parent.chapter_id} → Topic {parent.topic_id}
                    {parent.subtopic_id && ` → Subtopic ${parent.subtopic_id}`}
                    {parent.chunk_id && ` → Chunk ${parent.chunk_id}`}
                    {parent.micro_chunk_id && ` → Micro Chunk ${parent.micro_chunk_id}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentQAManagement;